import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { registerConfirmHost } from '../lib/confirm';

/**
 * ConfirmDialog — hôte de confirmation ET de saisie modale (sobre, chaud, animé).
 * Monté une fois à la racine. Sert confirmDialog() (=> bool) et promptDialog() (=> string|null).
 */
export default function ConfirmDialog() {
  const [req, setReq] = useState(null);
  const [value, setValue] = useState('');
  useEffect(() => registerConfirmHost(setReq), []);
  useEffect(() => { if (req) setValue(req.defaultValue || ''); }, [req]);

  const close = useCallback((confirmed) => {
    setReq((cur) => {
      if (cur) {
        if (cur.input) cur.resolve(confirmed ? (value.trim() || null) : null);
        else cur.resolve(!!confirmed);
      }
      return null;
    });
  }, [value]);

  useEffect(() => {
    if (!req) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); close(true); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [req, close]);

  return (
    <AnimatePresence>
      {req && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={() => close(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.22, 0.61, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="alertdialog" aria-modal="true"
            style={{ width: 'min(440px, 100%)', background: 'var(--widget-bg)', border: '1px solid var(--border-soft)', borderRadius: 14, boxShadow: '0 30px 80px rgba(0,0,0,0.5)', padding: 24 }}
          >
            <h3 className="font-serif" style={{ fontSize: 19, fontWeight: 400, letterSpacing: '-0.01em', color: 'var(--text-primary)' }}>{req.title}</h3>
            <p style={{ marginTop: 10, fontSize: 13.5, lineHeight: 1.5, color: 'var(--text-secondary)', whiteSpace: 'pre-line' }}>{req.message}</p>

            {req.input && (
              <input
                autoFocus
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={req.placeholder}
                style={{ marginTop: 16, width: '100%', height: 42, padding: '0 13px', borderRadius: 9, fontSize: 13.5, color: 'var(--text-primary)', background: 'var(--bg-tertiary)', border: '1px solid var(--border-soft)', outline: 'none' }}
              />
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={() => close(false)}
                style={{ height: 40, padding: '0 16px', borderRadius: 9, fontSize: 13, fontWeight: 500, cursor: 'pointer', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-soft)' }}
              >
                {req.cancelLabel}
              </button>
              <button
                autoFocus={!req.input}
                onClick={() => close(true)}
                style={{ height: 40, padding: '0 18px', borderRadius: 9, fontSize: 13, fontWeight: 500, cursor: 'pointer', border: '1px solid transparent', background: req.danger ? '#B23B36' : 'var(--text-primary)', color: req.danger ? '#FAFAF9' : 'var(--bg-primary)' }}
              >
                {req.confirmLabel}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
